/**
 * Global axios configuration:
 *  - Enable httpOnly cookies on every request (`withCredentials=true`)
 *  - Echo the CSRF double-submit cookie as `X-CSRF-Token` on every state-changing request
 *  - During the transition window we ALSO send the localStorage bearer token if present,
 *    so existing pages keep working until they're fully migrated.
 *  - 401 responses automatically clear auth state and bounce to the login page.
 */
import axios from 'axios';

const CSRF_COOKIE = 'uske_csrf';
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const getCookie = (name) => {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
};

let interceptorsInstalled = false;

export const setupAxios = () => {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  // Always send cookies (httpOnly access + CSRF)
  axios.defaults.withCredentials = true;

  axios.interceptors.request.use((config) => {
    // CSRF: double-submit pattern — only set on state-changing requests
    if (config.method && UNSAFE_METHODS.has(config.method.toLowerCase())) {
      const csrf = getCookie(CSRF_COOKIE);
      if (csrf) {
        config.headers = config.headers || {};
        if (!config.headers['X-CSRF-Token']) {
          config.headers['X-CSRF-Token'] = csrf;
        }
      }
    }

    // Backwards-compat: also send Bearer token from localStorage if present and not already set.
    // Callers that explicitly pass Authorization continue to work unchanged.
    if (!config.headers || !config.headers.Authorization) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        } catch (e) { /* ignore */ }
        // Only redirect if we're not already on a login page (avoid bounce loops on the public landing)
        const path = window.location.pathname;
        // Portal hostname behaviour: portal pages live at the root, so /login IS the portal login.
        const portalHost = (process.env.REACT_APP_PORTAL_HOSTNAME || '').trim().toLowerCase();
        const isOnPortal = portalHost && window.location.hostname.toLowerCase() === portalHost;
        const isPublic = path === '/' || path.startsWith('/login') || path.startsWith('/portal/login')
          || path === '/portal' || path.startsWith('/register') || path.startsWith('/products') || path.startsWith('/track-order');
        if (!isPublic) {
          let target;
          if (isOnPortal) target = '/login';
          else if (path.startsWith('/portal') || path.startsWith('/gov')) target = '/portal/login';
          else target = '/login';
          window.location.replace(target);
        }
      }
      return Promise.reject(error);
    }
  );
};
